import { Group, SegmentedControl } from '@mantine/core';
import { VisibilityState } from '@tanstack/table-core';
import { MRT_RowData, MRT_TableInstance } from 'mantine-react-table';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  harvestColState,
  resultsColState,
  sowingColState,
} from '../interculture-table/use-table';

import { useAuth } from '@auth/data-access';
import { hasAdminAccess, ROLES } from '@data-access';

interface ColumnSegmentedControlProps {
  table: MRT_TableInstance<MRT_RowData>;
}

enum ColumnType {
  Sowing = 'sowing',
  Harvest = 'harvest',
  Results = 'results',
}

export const ColumnSegmentedControl = ({
  table,
}: ColumnSegmentedControlProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<ColumnType>(ColumnType.Harvest);
  const { user } = useAuth();
  const isAdmin = hasAdminAccess(user?.user_access);

  const handleChange = useCallback(
    (v: string) => {
      setValue(v as ColumnType);
      switch (v) {
        case ColumnType.Sowing:
          table.setColumnVisibility(sowingColState as VisibilityState);
          break;
        case ColumnType.Harvest:
          table.setColumnVisibility(harvestColState as VisibilityState);
          break;
        case ColumnType.Results:
          table.setColumnVisibility(resultsColState as VisibilityState);
          break;
        default:
          table.setColumnVisibility(sowingColState as VisibilityState);
          break;
      }
    },
    [table],
  );

  return (
    <Group gap="md">
      <SegmentedControl
        color="pf-green"
        data={[
          {
            value: ColumnType.Sowing,
            label: t('interculture.segmentedControl.sowing'),
          },
          {
            value: ColumnType.Harvest,
            label: t('interculture.segmentedControl.harvest'),
          },
          ...(isAdmin
            ? [
                {
                  value: ColumnType.Results,
                  label: t('interculture.segmentedControl.results'),
                },
              ]
            : []),
        ]}
        onChange={handleChange}
        radius="xs"
        value={value}
      />
    </Group>
  );
};

export default ColumnSegmentedControl;
